import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

function InfoWithHover({ description }) {
  const [show, setShow] = useState(false);

  if (!description) {
    return (
      <span style={{ position: "relative", display: "inline-flex", alignItems: "center", verticalAlign: "middle", cursor: "pointer", lineHeight: 1, marginLeft: 4 }}>
        <InfoOutlinedIcon sx={{ fontSize: 16, color: "#6b7280" }} />
      </span>
    );
  }

  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center", verticalAlign: "middle", lineHeight: 1, marginLeft: 4 }}>
      <button
        type="button"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
        aria-label="Show information"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          lineHeight: 1,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16, color: "#6b7280" }} />
      </button>
      {show && (
        <span style={{
          position: "absolute",
          top: "calc(100% + 8px)",
          left: "50%",
          transform: "translateX(-50%)",
          maxWidth: 260,
          width: "max-content",
          background: "#111827",
          color: "#fff",
          padding: "6px 8px",
          borderRadius: 4,
          boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
          zIndex: 9999,
          fontSize: 10,
          lineHeight: 1.3,
          whiteSpace: "normal",
          pointerEvents: "none"
        }}>
          <span style={{
            position: "absolute",
            top: -6,
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderBottom: "6px solid #111827"
          }} />
          {description}
        </span>
      )}
    </span>
  );
}

InfoWithHover.propTypes = {
  description: PropTypes.string,
};

const WeightageTopNDialog = ({
  open,
  onClose,
  weightage,
  setWeightage,
  topNValue,
  setTopNValue,
  weightageError,
  validateWeightage,
  dialogLoading,
  onSubmit,
  showTimeThreshold = false,
  timeValue,
  setTimeValue,
  timeLabel = "Time (hours)"
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Weightage Percentage</DialogTitle>

      <DialogContent>
        {/* Weighted Avg */}
        <div style={{ marginBottom: 16 }}>
          <h4>Weighted Average (%)</h4>

          <TextField
            label="Count (%)"
            type="number"
            fullWidth
            margin="dense"
            value={weightage.Count}
            onChange={(e) => {
              const updated = { ...weightage, Count: e.target.value };
              setWeightage(updated);
              validateWeightage(updated);
            }}
          />

          <TextField
            label="Volume (%)"
            type="number"
            fullWidth
            margin="dense"
            value={weightage.Volume}
            onChange={(e) => {
              const updated = { ...weightage, Volume: e.target.value };
              setWeightage(updated);
              validateWeightage(updated);
            }}
          />

          <TextField
            label="Value (%)"
            type="number"
            fullWidth
            margin="dense"
            value={weightage.Value}
            onChange={(e) => {
              const updated = { ...weightage, Value: e.target.value };
              setWeightage(updated);
              validateWeightage(updated);
            }}
          />

        </div>

        {showTimeThreshold && (
          <>
            <hr />

            {/* Time Threshold */}
            <div style={{ marginBottom: 16 }}>
              <h4>
                Time Threshold (hours)
                <InfoWithHover description='For Priority Ranking based completely on weightage average provide "-" below.' />
              </h4>

              <TextField
                label={timeLabel}
                type="text"
                fullWidth
                margin="dense"
                value={timeValue}
                onChange={(e) => {
                  const raw = e.target.value.replaceAll(/[^0-9-]/g, "");
                  if (raw === "-" || raw === "" || /^\d+$/.test(raw)) {
                    setTimeValue(raw);
                  }
                }}
              />
            </div>
          </>
        )}

        {weightageError && (
          <div style={{ color: "red", fontSize: 12 }}>
            {weightageError}
          </div>
        )}

        <hr />

        {/* Top N */}
        <div>
          <h4>Top N Configuration</h4>

          <TextField
            label="Top N Value"
            type="number"
            fullWidth
            margin="dense"
            value={topNValue}
            onChange={(e) => {
              const value = e.target.value;
              setTopNValue(value);
              validateWeightage(weightage, value);
            }}
            slotProps={{ htmlInput: { min: 1 } }}
          />
        </div>

        {dialogLoading && (
          <div style={{ textAlign: "center", marginTop: 10 }}>
            <CircularProgress size={22} />
            <div style={{ fontSize: 12 }}>
              Updating configuration...
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          sx={{
            color: "#3447d4",
            textTransform: "none"
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={!!weightageError || dialogLoading}
          sx={{
            backgroundColor: "#3447d4",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#2b3bb3"
            },
            "&.Mui-disabled": {
              backgroundColor: "#aab2f5",
              color: "#ffffff"
            }
          }}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

WeightageTopNDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  weightage: PropTypes.shape({
    Count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Volume: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  setWeightage: PropTypes.func,
  topNValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setTopNValue: PropTypes.func,
  weightageError: PropTypes.string,
  validateWeightage: PropTypes.func,
  dialogLoading: PropTypes.bool,
  onSubmit: PropTypes.func,
  showTimeThreshold: PropTypes.bool,
  timeValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setTimeValue: PropTypes.func,
  timeLabel: PropTypes.string,
};

export default WeightageTopNDialog;